/// Sprites and frame-sequenced animation, mirroring Flame's Sprite,
/// SpriteAnimation and SpriteAnimationComponent.
///
/// A stepTime of Infinity means a single static frame, exactly as
/// `double.infinity` does in the Dart source.

import { PositionComponent } from './component';
import type { Anchor, Vec2 } from './vector';
import { imageFromCache } from './assets';

export interface Sprite {
  image: HTMLImageElement;
  srcX: number;
  srcY: number;
  srcW: number;
  srcH: number;
}

export function sprite(
  path: string,
  srcSize: Vec2,
  srcPosition: Vec2 = { x: 0, y: 0 },
): Sprite {
  return {
    image: imageFromCache(path),
    srcX: srcPosition.x,
    srcY: srcPosition.y,
    srcW: srcSize.x,
    srcH: srcSize.y,
  };
}

export class SpriteAnimation {
  readonly frames: readonly Sprite[];
  readonly stepTime: number;

  private elapsed = 0;
  private index = 0;

  constructor(frames: readonly Sprite[], stepTime: number) {
    this.frames = frames;
    this.stepTime = stepTime;
  }

  /** Single static frame from a whole image or a sub-rect of one. */
  static single(path: string, srcSize: Vec2, srcPosition?: Vec2): SpriteAnimation {
    return new SpriteAnimation([sprite(path, srcSize, srcPosition)], Number.POSITIVE_INFINITY);
  }

  /** Horizontal strip of `amount` frames, each `textureSize` wide. */
  static sequenced(
    path: string,
    amount: number,
    stepTime: number,
    textureSize: Vec2,
  ): SpriteAnimation {
    const frames: Sprite[] = [];
    for (let i = 0; i < amount; i += 1) {
      frames.push(sprite(path, textureSize, { x: textureSize.x * i, y: 0 }));
    }
    return new SpriteAnimation(frames, stepTime);
  }

  get currentFrame(): Sprite {
    return this.frames[this.index];
  }

  update(dt: number): void {
    if (this.frames.length <= 1 || !Number.isFinite(this.stepTime)) return;

    this.elapsed += dt;
    while (this.elapsed >= this.stepTime) {
      this.elapsed -= this.stepTime;
      this.index = (this.index + 1) % this.frames.length;
    }
  }

  reset(): void {
    this.elapsed = 0;
    this.index = 0;
  }
}

export class SpriteComponent extends PositionComponent {
  sprite: Sprite | null;

  constructor(options: {
    sprite?: Sprite | null;
    position?: Vec2;
    size?: Vec2;
    anchor?: Anchor;
    priority?: number;
  } = {}) {
    super(options);
    this.sprite = options.sprite ?? null;
  }

  override render(ctx: CanvasRenderingContext2D, originX: number, originY: number): void {
    const frame = this.sprite;
    if (!frame) return;
    drawSprite(ctx, frame, originX, originY, this.size.x, this.size.y);
  }
}

export class SpriteAnimationComponent extends PositionComponent {
  animation: SpriteAnimation | null;

  constructor(options: {
    animation?: SpriteAnimation | null;
    position?: Vec2;
    size?: Vec2;
    anchor?: Anchor;
    priority?: number;
  } = {}) {
    super(options);
    this.animation = options.animation ?? null;
  }

  override update(dt: number): void {
    this.animation?.update(dt);
  }

  override render(ctx: CanvasRenderingContext2D, originX: number, originY: number): void {
    const frame = this.animation?.currentFrame;
    if (!frame) return;
    drawSprite(ctx, frame, originX, originY, this.size.x, this.size.y);
  }
}

export function drawSprite(
  ctx: CanvasRenderingContext2D,
  frame: Sprite,
  x: number,
  y: number,
  width: number,
  height: number,
): void {
  ctx.drawImage(
    frame.image,
    frame.srcX,
    frame.srcY,
    frame.srcW,
    frame.srcH,
    x,
    y,
    width,
    height,
  );
}
